import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { trackViewContent, trackInitiateCheckout, trackVideoPlay, trackAddPaymentInfo } from '../utils/metaPixel';
import Header from '../components/Header';
import SEO from '../components/SEO';
import { API_URL } from '../config';
import { 
  Clock, Award, CheckCircle, Shield, Eye, Lock, 
  Play, ChevronDown, ArrowRight, Users, CreditCard,
  Target, Sparkles, BookOpen, Zap, Heart, Quote,
  Monitor, Smartphone, Mail, Settings, X, Check,
  HelpCircle, RefreshCw, FileText, UserCheck, ChevronRight,
  Volume2
} from 'lucide-react';
import '../styles/CourseLanding.css';


// Presenter image - Jack
const PRESENTER_IMAGE = 'https://customer-assets.emergentagent.com/job_edupages-2/artifacts/k4f5b9sc_image.png';

// Supporting human photos
const HUMAN_PHOTOS = {
  heroBackground: 'https://images.unsplash.com/photo-1614793351079-11dd79b922ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjB1c2luZyUyMGxhcHRvcCUyMGhvbWUlMjBjb3p5JTIwd2FybSUyMG5hdHVyYWwlMjBsaWdodHxlbnwwfHx8fDE3Njk5Mjk3MjN8MA&ixlib=rb-4.1.0&q=85',
};

const CourseLanding = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [landingContent, setLandingContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showStickyCta, setShowStickyCta] = useState(false);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  
  // Video preview modal state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [guideCardRevealed, setGuideCardRevealed] = useState(false);
  const guideCardRef = useRef(null);
  
  // Inline video player state
  const [videoHighlighted, setVideoHighlighted] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoKey, setVideoKey] = useState(0); // Key to force iframe reload
  const inlineVideoRef = useRef(null);

  // Refs for scroll detection
  const heroRef = useRef(null);
  const finalCtaRef = useRef(null);

  // Check for discount code from URL or localStorage on mount
  useEffect(() => {
    const urlDiscount = searchParams.get('discount');
    
    let storedDiscount = null;
    try {
      const stored = localStorage.getItem('huntDiscount');
      if (stored) {
        const data = JSON.parse(stored);
        console.log('[CourseLanding] Found stored voucher:', data);
        
        // Check if voucher is valid (not expired and not claimed)
        if (data.expires > Date.now() && !data.claimed) {
          storedDiscount = data.code;
          console.log('[CourseLanding] Using stored voucher code:', storedDiscount);
        } else if (data.expires <= Date.now()) {
          console.log('[CourseLanding] Voucher expired, removing');
          localStorage.removeItem('huntDiscount');
        } else if (data.claimed) {
          console.log('[CourseLanding] Voucher already claimed');
        }
      }
    } catch (e) {
      console.error('[CourseLanding] Error reading voucher:', e);
    }
    
    // URL discount takes priority, then stored discount
    const codeToUse = urlDiscount || storedDiscount;
    if (codeToUse) {
      console.log('[CourseLanding] Applying discount code:', codeToUse);
      setDiscountCode(codeToUse);
    }
  }, [searchParams]);

  // Scroll progress + sticky CTA visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(Math.min(progress, 100));
      
      // Show sticky CTA after scrolling past hero
      const heroBottom = heroRef.current?.getBoundingClientRect().bottom || 0;
      const finalCtaTop = finalCtaRef.current?.getBoundingClientRect().top || window.innerHeight;
      setShowStickyCta(heroBottom < 0 && finalCtaTop > window.innerHeight * 0.5);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for scroll reveal - DISABLED on mobile for performance
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    
    // On mobile, skip all reveal animations - show everything immediately
    if (isMobile) {
      const allElements = document.querySelectorAll('.reveal-section, .reveal-item');
      allElements.forEach(el => el.classList.add('revealed'));
      return; // Exit early - no observer needed
    }
    
    // Desktop only: use intersection observer
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          const children = entry.target.querySelectorAll('.reveal-item');
          children.forEach((child, index) => {
            setTimeout(() => {
              child.classList.add('revealed');
            }, index * 80);
          });
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('.reveal-section');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, [course]);

  // Fetch course data
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/courses/${slug}`);
        const courseData = response.data;
        setCourse(courseData);
        
        // Track ViewContent when course page loads
        trackViewContent({
          content_name: courseData.title,
          content_id: courseData.slug || courseData.id,
          value: courseData.price_cents ? courseData.price_cents / 100 : 0
        });
        
        // Validate discount if present
        if (discountCode) {
          validateDiscount(discountCode);
        }
      } catch (err) {
        console.error('Error fetching course:', err);
        navigate('/courses');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [slug, navigate, discountCode]);

  const validateDiscount = async (code) => {
    if (!code || !course) return;
    setValidatingDiscount(true);
    try {
      const response = await axios.post(`${API_URL}/api/stripe/validate-discount`, {
        code: code,
        course_slug: course.slug  // Fixed: API expects course_slug, not course_id
      });
      setDiscountInfo(response.data);
      console.log('[CourseLanding] Discount validation result:', response.data);
    } catch (err) {
      console.error('[CourseLanding] Discount validation error:', err);
      setDiscountInfo({ valid: false });
    } finally {
      setValidatingDiscount(false);
    }
  };

  useEffect(() => {
    if (discountCode && course) {
      validateDiscount(discountCode);
    }
  }, [discountCode, course]);

  const handlePurchase = async () => {
    if (!course) return;
    
    if (course.has_access) {
      navigate(`/course/${course.id}`);
      return;
    }
    
    // Calculate actual price
    const basePrice = course.price_cents ? course.price_cents / 100 : 0;
    const finalPrice = discountInfo?.valid 
      ? basePrice * (1 - (discountInfo.discount_percent / 100))
      : basePrice;
    
    // Track InitiateCheckout
    trackInitiateCheckout({
      content_name: course.title,
      content_id: course.slug || course.id,
      value: finalPrice
    });
    
    setPurchasing(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/stripe/guest-checkout`,
        { 
          course_slug: course.slug,
          discount_code: discountInfo?.valid ? discountCode : null
        }
      );
      
      if (response.data.url) {
        // Track AddPaymentInfo before redirect to Stripe
        trackAddPaymentInfo({
          content_name: course.title,
          content_id: course.slug || course.id,
          value: finalPrice,
          payment_method: 'card'
        });
        
        // Mark voucher as claimed and remove from storage
        try {
          const stored = localStorage.getItem('huntDiscount');
          if (stored) {
            const data = JSON.parse(stored);
            data.claimed = true;
            localStorage.setItem('huntDiscount', JSON.stringify(data));
            console.log('[CourseLanding] Voucher marked as claimed');
          }
        } catch (e) {}
        
        // Clear the hunt discount after successful checkout redirect
        localStorage.removeItem('huntDiscount');
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      alert(err.response?.data?.detail || 'Error starting checkout');
    } finally {
      setPurchasing(false);
    }
  };

  // Handle ESC key to close modal - must be before any returns
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showVideoModal) {
          setShowVideoModal(false);
          document.body.style.overflow = '';
        }
        if (showGuideModal) {
          setShowGuideModal(false);
          document.body.style.overflow = '';
          // Return focus to guide card
          guideCardRef.current?.focus();
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showVideoModal, showGuideModal]);

  // Trigger staggered reveal animation for guide card features
  useEffect(() => {
    if (course && !guideCardRevealed) {
      const timer = setTimeout(() => {
        setGuideCardRevealed(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [course, guideCardRevealed]);

  // Scroll to video and highlight
  const scrollToVideo = useCallback((e) => {
    if (e) e.preventDefault();
    const videoContainer = document.querySelector('.guide-video-container');
    if (videoContainer) {
      videoContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setVideoHighlighted(true);
      setTimeout(() => setVideoHighlighted(false), 2000);
    }
  }, []);

  if (loading) {
    return (
      <>
        <Header />
        <div className="course-landing-loading">
          <div className="loading-spinner" />
          <p>Loading course...</p>
        </div>
      </>
    );
  }

  if (!course) return null;

  const content = landingContent || getDefaultLandingContent(course);
  const originalPrice = (course.price_cents / 100).toFixed(0);
  const discountedPrice = discountInfo?.valid 
    ? (discountInfo.discounted_price / 100).toFixed(0)
    : null;
  const price = discountedPrice || originalPrice;

  // Scroll to preview section
  const scrollToPreview = (e) => {
    e.preventDefault();
    document.getElementById('preview-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Open video modal
  const openVideoModal = (e) => {
    e.preventDefault();
    setShowVideoModal(true);
    document.body.style.overflow = 'hidden';
  };

  // Close video modal
  const closeVideoModal = () => {
    setShowVideoModal(false);
    document.body.style.overflow = '';
  };

  // Open guide intro video modal
  const openGuideModal = (e) => {
    if (e) e.preventDefault();
    setShowGuideModal(true);
    document.body.style.overflow = 'hidden';
  };

  // Close guide modal
  const closeGuideModal = () => {
    setShowGuideModal(false);
    document.body.style.overflow = '';
    guideCardRef.current?.focus();
  };

  // Handle guide card keyboard interaction
  const handleGuideCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openGuideModal();
    }
  };

  // Structured data for this course (Organization + Course + FAQPage schema)
  const courseStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "name": "All Secured",
        "url": "https://allsecured.com.au/",
        "logo": "https://customer-assets.emergentagent.com/job_0e5aee2f-27de-4cc5-bc22-1ba27f01672b/artifacts/f8dzsnko_Variant%201_Shield%20Only_Vector.png",
        "description": "Practical digital safety and cyber privacy education for everyday people"
      },
      {
        "@type": "Course",
        "name": course.title,
        "description": "A practical online course designed to help you reduce silent tracking and profiling, prevent scams, understand safety tools like VPN and reset your digital security. No jargon. Step by step.",
        "provider": {
          "@type": "Organization",
          "name": "All Secured",
          "url": "https://allsecured.com.au/"
        },
        "url": `https://allsecured.com.au/courses/${course.slug}/`,
        "offers": {
          "@type": "Offer",
          "price": price,
          "priceCurrency": "AUD",
          "availability": "https://schema.org/InStock",
          "url": `https://allsecured.com.au/courses/${course.slug}/`
        },
        "hasCourseInstance": {
          "@type": "CourseInstance",
          "courseMode": "online",
          "duration": `PT${course.duration_minutes || 50}M`
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Do I need any technical knowledge to take this course?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No technical knowledge is required. This course is designed for everyday users of Facebook, Instagram, Google, email and smartphones. Everything is explained in plain language with step-by-step guidance."
            }
          },
          {
            "@type": "Question",
            "name": "How long does the course take to complete?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The course is designed to be completed in under an hour. You can work through it at your own pace and return to any section as needed."
            }
          },
          {
            "@type": "Question",
            "name": "What devices can I use for this course?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "You can access the course on any device with a web browser - computer, tablet, or smartphone. The course works best when you can follow along on the device you want to secure."
            }
          },
          {
            "@type": "Question",
            "name": "Is there a money-back guarantee?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, we offer a full refund if you're not satisfied with the course. Contact us within 14 days of purchase for a complete refund, no questions asked."
            }
          },
          {
            "@type": "Question",
            "name": "What will I learn in this course?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "You'll learn to tighten privacy settings on social media, secure your devices and accounts, understand how tracking works, set up basic security tools, and develop habits that protect your digital life ongoing."
            }
          }
        ]
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Cyber Privacy & Security Basics Course | Limit Tracking and Profiling & Protect Your Digital Life"
        description="A practical online course designed to help you reduce silent tracking and profiling, prevent scams, understand safety tools like VPN and reset your digital security. No jargon. Step by step."
        canonical={`/courses/${course.slug}`}
        ogType="product"
        structuredData={courseStructuredData}
      />
      <Header />
      
      {/* Scroll Progress Indicator */}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />
      
      <div className="course-landing">
        {/* ============================================
            HERO SECTION - Optimized for Mobile Conversion
            ============================================ */}
        <section className="cl-hero reveal-section" ref={heroRef}>
          {/* Premium animated background */}
          <div className="cl-hero-bg-animated" aria-hidden="true">
            <div className="cl-hero-glow cl-hero-glow-1"></div>
            <div className="cl-hero-glow cl-hero-glow-2"></div>
            <div className="cl-hero-glow cl-hero-glow-3"></div>
            <div className="cl-hero-shimmer"></div>
          </div>
          <div className="cl-hero-motif" aria-hidden="true"></div>
          <div className="cl-hero-bg-photo" style={{ backgroundImage: `url(${HUMAN_PHOTOS.heroBackground})` }} />
          
          <div className="cl-hero-content">
            <div className="cl-hero-badge reveal-item">
              <Shield size={16} />
              <span>Cybersecurity Course</span>
            </div>
            
            <h1 className="reveal-item">{course.title}</h1>
            <p className="cl-hero-tagline reveal-item">
              Tighten the settings that quietly expose you — <span className="text-highlight">in under an hour</span>.
            </p>
            
            {/* CTA - Moved up for mobile priority */}
            <div className="cl-hero-cta reveal-item">
              <button 
                className="btn-primary-lg hero-cta-btn"
                onClick={handlePurchase}
                disabled={purchasing}
                data-testid="hero-buy-btn"
              >
                {purchasing ? 'Processing...' : course.has_access ? 'Continue Learning' : (
                  discountedPrice ? (
                    <>
                      Start Your Security Reset — <span className="price-original">${originalPrice}</span> ${price} →
                    </>
                  ) : `Start Your Security Reset — $${price} →`
                )}
              </button>
              
              {/* Trust text directly below CTA */}
              {!course.has_access && (
                <div className="cl-cta-trust-block">
                  <p className="cl-trust-line">One-time payment. Instant access.</p>
                  
                  {/* Includes line */}
                  <p className="cl-includes-line">
                    <span>Includes:</span> Video walkthrough • Checklist • Quick self-check • Instant access
                  </p>
                  
                  {/* Start smaller link */}
                  <p className="cl-start-smaller">
                    Prefer self-guided? <Link to="/course-1-blueprint" className="cl-start-smaller-link" onClick={() => window.scrollTo(0, 0)}>Start with the PDF Blueprint for $28</Link> (upgrade later — $28 credited).
                  </p>
                  
                  {/* Secondary link to guide video */}
                  <button 
                    className="cl-guide-preview-link"
                    onClick={scrollToVideo}
                    type="button"
                  >
                    Not sure yet? Watch the 1:02 guide intro →
                  </button>
                </div>
              )}
              
              {/* Preview 90 seconds link */}
              <button 
                className="cl-preview-90-btn"
                onClick={openVideoModal}
                type="button"
              >
                <Play size={14} />
                Preview 90 seconds
              </button>
              
              {/* Discount badge */}
              {discountInfo?.valid && (
                <div className="cl-discount-badge" data-testid="discount-badge">
                  <Sparkles size={14} />
                  <span>
                    {discountCode === 'FOREST8' 
                      ? '8% Cyber Forest discount applied!' 
                      : `${discountInfo.discount_percent}% off applied!`}
                  </span>
                </div>
              )}
            </div>
            
            {/* Outcome chips - Below CTA on mobile */}
            <div className="cl-outcome-chips reveal-item">
              <div className="outcome-chip">
                <Eye size={14} />
                <span>Less tracking + profiling</span>
              </div>
              <div className="outcome-chip">
                <Users size={14} />
                <span>Harder to find you</span>
              </div>
              <div className="outcome-chip">
                <Settings size={14} />
                <span>Cleaner app access</span>
              </div>
              <div className="outcome-chip">
                <Shield size={14} />
                <span>Stronger account safety</span>
              </div>
            </div>
          </div>
          
          {/* VIDEO PREVIEW CARD - Inline autoplaying video */}
          <div className="cl-hero-visual reveal-item">
            <div 
              className={`guide-video-container ${videoHighlighted ? 'highlighted' : ''}`}
              ref={guideCardRef}
              data-testid="guide-video-container"
            >
              {/* Label */}
              <div className="guide-video-label">
                <Play size={12} />
                <span>MEET YOUR GUIDE</span>
                <span className="guide-video-duration">1:02</span>
              </div>
              
              {/* Inline autoplaying video - pause when modal is open */}
              <div className="guide-video-wrapper">
                {!showGuideModal && (
                  <iframe 
                    key={videoKey}
                    ref={inlineVideoRef}
                    src={`https://player.vimeo.com/video/1169776582?autoplay=1&loop=1&muted=${videoMuted ? 1 : 0}&background=${videoMuted ? 1 : 0}&badge=0&autopause=0&player_id=0&app_id=58479&controls=${videoMuted ? 0 : 1}&texttrack=false`}
                    className="guide-video-iframe"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title="Meet Your Guide - Course Intro"
                  />
                )}
                
                {/* Show static thumbnail when modal is open (video paused) */}
                {showGuideModal && (
                  <div className="guide-video-paused-overlay">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_edupages-2/artifacts/k4f5b9sc_image.png" 
                      alt="Video paused"
                      className="guide-video-paused-thumbnail"
                    />
                    <div className="guide-video-paused-icon">
                      <Play size={32} fill="currentColor" />
                    </div>
                  </div>
                )}
                
                {/* "Watch with sound" clickable button - ONE CLICK to unmute */}
                {videoMuted && !showGuideModal && (
                  <button 
                    className="guide-video-sound-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Unmute and restart video from beginning
                      setVideoMuted(false);
                      setVideoKey(prev => prev + 1); // Force iframe reload
                    }}
                    aria-label="Click to watch with sound"
                    data-testid="guide-video-sound-btn"
                  >
                    <Volume2 size={16} />
                    <span>Watch with sound</span>
                  </button>
                )}
                
                {/* Expand button - opens modal */}
                <button 
                  className="guide-video-expand-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowGuideModal(true);
                    document.body.style.overflow = 'hidden';
                  }}
                  aria-label="Expand video to full screen"
                  data-testid="guide-video-expand-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <polyline points="9 21 3 21 3 15"></polyline>
                    <line x1="21" y1="3" x2="14" y2="10"></line>
                    <line x1="3" y1="21" x2="10" y2="14"></line>
                  </svg>
                </button>
              </div>
              
              {/* Brief tagline under video */}
              <p className="guide-video-tagline">
                <span className="guide-video-micro-label">Guided walkthrough (video)</span>
                Follow along step-by-step — we show every screen.
              </p>
            </div>
          </div>
        </section>

        {/* Video Preview Modal (existing) */}
        {showVideoModal && (
          <div className="cl-video-modal-overlay" onClick={closeVideoModal}>
            <div className="cl-video-modal" onClick={(e) => e.stopPropagation()}>
              <button className="cl-video-modal-close" onClick={closeVideoModal} aria-label="Close video">
                <X size={24} />
              </button>
              <div className="cl-video-modal-content">
                <iframe 
                  src="https://player.vimeo.com/video/1166189270?h=17a7a06214&badge=0&autopause=0&player_id=0&app_id=58479"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  allowFullScreen
                  title="Course Preview - Cyber Privacy &amp; Security Basics"
                />
              </div>
            </div>
          </div>
        )}

        {/* Guide Intro Video Modal */}
        {showGuideModal && (
          <div 
            className="cl-guide-modal-overlay" 
            onClick={closeGuideModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="guide-modal-title"
          >
            <div className="cl-guide-modal" onClick={(e) => e.stopPropagation()}>
              <button 
                className="cl-guide-modal-close" 
                onClick={closeGuideModal} 
                aria-label="Close video"
                autoFocus
              >
                <X size={24} />
              </button>
              
              <h2 id="guide-modal-title" className="cl-guide-modal-title">
                Meet Your Guide (1:02)
              </h2>
              
              <div className="cl-guide-modal-video">
                <iframe 
                  src="https://player.vimeo.com/video/1169776582?autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  allowFullScreen
                  title="Meet Your Guide - Course Intro"
                />
              </div>
              
              <p className="cl-guide-modal-reassurance">
                No jargon. No scare tactics. You'll know when you're done.
              </p>
              
              <button 
                className="btn-primary-lg cl-guide-modal-cta"
                onClick={(e) => {
                  closeGuideModal();
                  handlePurchase(e);
                }}
                disabled={purchasing}
                data-testid="guide-modal-buy-btn"
              >
                {purchasing ? 'Processing...' : (
                  discountedPrice ? (
                    <>
                      Start Your Security Reset — <span className="price-original">${originalPrice}</span> ${price} →
                    </>
                  ) : `Start Your Security Reset — $${price} →`
                )}
              </button>
            </div>
          </div>
        )}

        {/* ============================================
            SOCIAL PROOF STRIP
            ============================================ */}
        <section className="cl-proof-strip reveal-section">
          <div className="cl-container">
            <div className="cl-testimonial-strip">
              <div className="testimonial-mini reveal-item">
                <Quote size={16} />
                <p>"Finally understood what all those settings actually do."</p>
                <span className="testimonial-author">— Sarah M.</span>
              </div>
              <div className="testimonial-mini reveal-item">
                <Quote size={16} />
                <p>"Clear, calm, no scare tactics. Exactly what I needed."</p>
                <span className="testimonial-author">— David T.</span>
              </div>
            </div>
          </div>
        </section>

        {/* Section Divider */}
        <div className="cl-section-divider" aria-hidden="true"></div>

        {/* ============================================
            PREVIEW SECTION - High Impact
            ============================================ */}
        <section id="preview-section" className="cl-section cl-preview reveal-section">
          <div className="cl-container">
            <div className="cl-section-header">
              <Play size={28} className="section-icon" />
              <h2>A quick look inside</h2>
            </div>
            
            <div className="cl-preview-content side-by-side">
              <div className="cl-preview-frame reveal-item">
                <div className="preview-video-container" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '12px' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/1166189270?h=17a7a06214&badge=0&autopause=0&player_id=0&app_id=58479"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                    allowFullScreen
                    title="Course Preview - Cyber Privacy &amp; Security Basics"
                  />
                </div>
              </div>
              
              <div className="cl-preview-steps-inline reveal-item">
                <h3>How each lesson works:</h3>
                <div className="preview-step-inline">
                  <div className="step-number-inline">1</div>
                  <div className="step-content-inline">
                    <strong>See the setting</strong>
                    <p>Visual walkthrough showing exactly where it is</p>
                  </div>
                </div>
                <div className="preview-step-inline">
                  <div className="step-number-inline">2</div>
                  <div className="step-content-inline">
                    <strong>Make the change</strong>
                    <p>Follow along at your own pace</p>
                  </div>
                </div>
                <div className="preview-step-inline">
                  <div className="step-number-inline">3</div>
                  <div className="step-content-inline">
                    <strong>Confirm it's done</strong>
                    <p>Know exactly what the result should look like</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="cl-section-cta reveal-item">
              <button 
                className="btn-primary-lg"
                onClick={handlePurchase}
                disabled={purchasing}
                data-testid="preview-cta-btn"
              >
                Start Your Security Reset
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </section>

        {/* Section Divider */}
        <div className="cl-section-divider" aria-hidden="true"></div>

        {/* ============================================
            CONSOLIDATED: WHAT YOU'LL CHANGE + WHY THIS WORKS
            ============================================ */}
        <section className="cl-section cl-outcomes-consolidated light reveal-section">
          <div className="cl-container">
            {/* Part 1: What you'll change today */}
            <div className="cl-outcomes-part">
              <div className="cl-section-header">
                <Target size={28} className="section-icon" />
                <h2>What you'll change today</h2>
              </div>
              
              <div className="cl-outcomes-grid-tight">
                <div className="outcome-card reveal-item">
                  <CheckCircle size={20} />
                  <span>Reduce personalised ads and cross-site tracking</span>
                </div>
                <div className="outcome-card reveal-item">
                  <CheckCircle size={20} />
                  <span>Limit how easily people can find you by phone or email</span>
                </div>
                <div className="outcome-card reveal-item">
                  <CheckCircle size={20} />
                  <span>Clean up old app access that quietly lingers</span>
                </div>
                <div className="outcome-card reveal-item">
                  <CheckCircle size={20} />
                  <span>Lock down microphone and camera permissions</span>
                </div>
                <div className="outcome-card reveal-item">
                  <CheckCircle size={20} />
                  <span>Protect photos from casual misuse</span>
                </div>
                <div className="outcome-card reveal-item">
                  <CheckCircle size={20} />
                  <span>Understand VPNs and encrypted email (without overthinking)</span>
                </div>
              </div>
            </div>
            
            {/* Part 2: Why this works */}
            <div className="cl-why-works-part">
              <h3>Why this approach works</h3>
              <div className="cl-why-grid">
                <div className="why-item reveal-item">
                  <div className="why-icon">
                    <Heart size={20} />
                  </div>
                  <div className="why-content">
                    <strong>Calm, not alarming</strong>
                    <p>No scare tactics. Just practical steps.</p>
                  </div>
                </div>
                <div className="why-item reveal-item">
                  <div className="why-icon">
                    <Eye size={20} />
                  </div>
                  <div className="why-content">
                    <strong>Visual walkthroughs</strong>
                    <p>See exactly where each setting lives.</p>
                  </div>
                </div>
                <div className="why-item reveal-item">
                  <div className="why-icon">
                    <Check size={20} />
                  </div>
                  <div className="why-content">
                    <strong>Clear confirmation</strong>
                    <p>Know when you're actually done.</p>
                  </div>
                </div>
                <div className="why-item reveal-item">
                  <div className="why-icon">
                    <RefreshCw size={20} />
                  </div>
                  <div className="why-content">
                    <strong>Builds habits</strong>
                    <p>Not just checklists you'll forget.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="cl-section-cta reveal-item">
              <button 
                className="btn-secondary-lg"
                onClick={handlePurchase}
                disabled={purchasing}
              >
                Get Started — ${price}
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </section>

        {/* Section Divider */}
        <div className="cl-section-divider" aria-hidden="true"></div>

        {/* ============================================
            CURRICULUM - Improved Accordion
            ============================================ */}
        {course.modules && course.modules.length > 0 && (
          <section className="cl-section cl-curriculum reveal-section">
            <div className="cl-container">
              <div className="cl-section-header">
                <BookOpen size={28} className="section-icon" />
                <h2>Course Curriculum</h2>
                <p className="curriculum-subtitle">{course.modules.length} modules • ~{course.duration_minutes || 50} minutes total</p>
              </div>
              
              <div className="cl-curriculum-accordion">
                {course.modules.map((module, idx) => (
                  <div 
                    key={module.id} 
                    className={`curriculum-accordion-item reveal-item revealed ${expandedModule === idx ? 'expanded' : ''}`}
                  >
                    <button 
                      className="curriculum-accordion-header"
                      onClick={() => setExpandedModule(expandedModule === idx ? null : idx)}
                    >
                      <div className="curriculum-header-left">
                        <span className="curriculum-number">{String(idx + 1).padStart(2, '0')}</span>
                        <div className="curriculum-title-block">
                          <h4>{module.title}</h4>
                          {module.duration_minutes && (
                            <span className="curriculum-duration">
                              <Clock size={12} /> {module.duration_minutes} min
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="curriculum-header-right">
                        <span className="curriculum-included-badge">
                          <Check size={14} /> Included
                        </span>
                        <ChevronDown size={18} className="accordion-chevron" />
                      </div>
                    </button>
                    <div className="curriculum-accordion-content">
                      <div className="curriculum-details">
                        <div className="curriculum-detail-row">
                          <span className="curriculum-label">What you'll do</span>
                          <p className="curriculum-text">{curriculumContent[idx]?.whatYouDo || "Configure key privacy settings"}</p>
                        </div>
                        <div className="curriculum-detail-row">
                          <span className="curriculum-label">What you'll confirm</span>
                          <p className="curriculum-text">{curriculumContent[idx]?.whatYouConfirm || "Clear visual confirmation"}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="cl-section-cta reveal-item">
                <button 
                  className="btn-primary-lg"
                  onClick={handlePurchase}
                  disabled={purchasing}
                >
                  Start Learning Now
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ============================================
            COURSE DETAILS - Compact
            ============================================ */}
        <section className="cl-section cl-details light reveal-section">
          <div className="cl-container">
            <div className="cl-details-grid-compact">
              <div className="detail-item-compact reveal-item">
                <Clock size={22} />
                <div>
                  <h4>~{course.duration_minutes || 50} min</h4>
                  <p>Total length</p>
                </div>
              </div>
              <div className="detail-item-compact reveal-item">
                <Play size={22} />
                <div>
                  <h4>Video</h4>
                  <p>Visual walkthroughs</p>
                </div>
              </div>
              <div className="detail-item-compact reveal-item">
                <Zap size={22} />
                <div>
                  <h4>Lifetime</h4>
                  <p>Access forever</p>
                </div>
              </div>
            </div>
            
            {/* Mini Comparison Block */}
            <div className="cl-format-comparison">
              <h3>Choose your format</h3>
              <div className="cl-format-cards">
                <button 
                  className="cl-format-card clickable"
                  onClick={async () => {
                    // Navigate to blueprint checkout for $28 PDF
                    try {
                      const response = await axios.post(`${API_URL}/api/blueprint/checkout`, {
                        product_type: 'pdf',
                        utm_params: {}
                      });
                      if (response.data.url) {
                        window.location.href = response.data.url;
                      }
                    } catch (error) {
                      console.error('Checkout error:', error);
                    }
                  }}
                  data-testid="format-card-pdf"
                >
                  <div className="cl-format-card-header">
                    <FileText size={20} />
                    <span className="cl-format-title">PDF Blueprint</span>
                    <span className="cl-format-price">$28</span>
                  </div>
                  <p className="cl-format-subtitle">Self-guided checklist (Read → Do → Tick)</p>
                  <ul className="cl-format-bullets">
                    <li>Checklist with checkpoints</li>
                    <li>One page per module</li>
                    <li>PDF delivered via email</li>
                  </ul>
                  <span className="cl-format-cta-text">Get PDF Blueprint →</span>
                </button>
                
                <button 
                  className="cl-format-card featured clickable"
                  onClick={handlePurchase}
                  disabled={purchasing}
                  data-testid="format-card-videos"
                >
                  <div className="cl-format-card-header">
                    <Play size={20} />
                    <span className="cl-format-title">Guided Videos + Blueprint</span>
                    <span className="cl-format-price">$88</span>
                  </div>
                  <p className="cl-format-subtitle">Guided walkthrough (Watch → Follow along → Confirm)</p>
                  <ul className="cl-format-bullets">
                    <li>Guided videos with examples</li>
                    <li>Step-by-step screen walkthroughs</li>
                    <li>Includes the PDF Blueprint</li>
                  </ul>
                  <span className="cl-format-cta-text">Get Guided Videos →</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Section Divider */}
        <div className="cl-section-divider" aria-hidden="true"></div>

        {/* ============================================
            FAQ SECTION - Objection Handling
            ============================================ */}
        <section className="cl-section cl-faq reveal-section">
          <div className="cl-container">
            <div className="cl-section-header">
              <HelpCircle size={28} className="section-icon" />
              <h2>Frequently Asked Questions</h2>
            </div>
            
            <div className="cl-faq-list">
              {faqItems.map((faq, idx) => (
                <div 
                  key={idx} 
                  className={`faq-item reveal-item revealed ${expandedFaq === idx ? 'expanded' : ''}`}
                >
                  <button 
                    className="faq-question"
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown size={20} />
                  </button>
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================
            FINAL CTA SECTION
            ============================================ */}
        <section className="cl-section cl-final-cta reveal-section" ref={finalCtaRef}>
          <div className="cl-container">
            <div className="cl-cta-content">
              <div className="cl-cta-card">
                <div className="cl-price-tag">
                  {discountedPrice && (
                    <span className="price-original-large">${originalPrice}</span>
                  )}
                  <span className="price-amount">${price}</span>
                  <span className="price-note">one-time payment</span>
                </div>
                
                <p className="cl-cta-no-upsells">
                  <span className="text-highlight">No subscription</span>. No upsells inside the course.
                </p>
                
                <button 
                  className="btn-primary-xl"
                  onClick={handlePurchase}
                  disabled={purchasing}
                  data-testid="final-cta-btn"
                >
                  {purchasing ? 'Processing...' : course.has_access ? 'Continue Learning' : 'Start Learning Now'}
                  <ArrowRight size={20} />
                </button>
                
                <div className="cl-cta-reassurance">
                  <p><Check size={14} /> Go at your own pace</p>
                  <p><Check size={14} /> Pause whenever you need</p>
                  <p><Check size={14} /> Come back anytime</p>
                </div>
                
                <p className="cl-checkout-legal-final">
                  By proceeding, you agree to our <Link to="/terms-of-use">Terms of Use</Link> and <Link to="/refund-policy">Refund Policy</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================
            STICKY CTA BAR (Mobile + Desktop)
            ============================================ */}
        {!course.has_access && (
          <div className={`cl-sticky-cta ${showStickyCta ? 'visible' : ''}`}>
            <div className="sticky-cta-content">
              <div className="sticky-cta-text">
                <span className="sticky-price">${price}</span>
                <span className="sticky-note">one-time</span>
              </div>
              <button 
                className="sticky-cta-btn"
                onClick={handlePurchase}
                disabled={purchasing}
                data-testid="sticky-cta-btn"
              >
                {purchasing ? 'Processing...' : 'Start Your Security Reset'}
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// Module-specific curriculum content
const curriculumContent = {
  0: {
    whatYouDo: "Set up your quick baseline and understand the reset flow.",
    whatYouConfirm: "You'll know exactly which accounts and devices you'll tighten in this course."
  },
  1: {
    whatYouDo: "Reduce ad personalisation and tracking signals across key platforms.",
    whatYouConfirm: "Ad personalisation shows as reduced/limited in your settings."
  },
  2: {
    whatYouDo: "Make it harder for strangers to find you via phone, email, and search.",
    whatYouConfirm: "Phone/email lookups and public discovery options are switched off or restricted."
  },
  3: {
    whatYouDo: "Tighten photo privacy and apply simple safeguards for sharing.",
    whatYouConfirm: "Your photo audience is limited (e.g., Friends-only) and sharing controls are set correctly."
  },
  4: {
    whatYouDo: "Remove old app connections and reduce lingering account access.",
    whatYouConfirm: "Unnecessary third-party access is removed from your connected apps list."
  },
  5: {
    whatYouDo: "Choose a simple VPN rule that fits your life (without overthinking).",
    whatYouConfirm: "You'll know exactly when a VPN helps, and when it's optional."
  },
  6: {
    whatYouDo: "Start a practical encrypted email plan that you can actually follow.",
    whatYouConfirm: "You'll have a clear next step (provider chosen or setup path selected)."
  },
  7: {
    whatYouDo: "Audit and tighten microphone permissions across your device/apps.",
    whatYouConfirm: "Microphone access is restricted to only what you choose."
  },
  8: {
    whatYouDo: "Run a final sweep to catch the easy-to-miss settings.",
    whatYouConfirm: "Your key settings match the course's 'secure baseline' checklist."
  },
  9: {
    whatYouDo: "Lock in your habit loop and set a simple refresh routine.",
    whatYouConfirm: "You'll leave with a repeatable check-in plan (what to revisit + when)."
  }
};

// FAQ Items - Objection Handling
const faqItems = [
  {
    question: 'Do I need any technical knowledge?',
    answer: 'Not at all! This course is specifically designed for non-technical users. We use plain language and visual walkthroughs to guide you through every step. If you can use Facebook or email, you can do this.'
  },
  {
    question: 'Will this break my apps or change how I use my phone?',
    answer: 'No. The changes we guide you through are about privacy settings, not functionality. Your apps will work exactly the same — they\'ll just collect less of your data.'
  },
  {
    question: 'What platforms does this cover?',
    answer: 'The course covers Facebook, Instagram, Google, email providers, and smartphones (both iPhone and Android). These are the tools most people use daily.'
  },
  {
    question: 'How long will it take?',
    answer: 'The full course is about 50–55 minutes of video. You can complete it in one sitting, or break it into sections. Most people finish in under an hour.'
  },
  {
    question: 'Is this a subscription?',
    answer: 'No. It\'s a one-time payment of $88. You get lifetime access. There are no recurring charges, no upsells, and no hidden fees.'
  },
  {
    question: 'What if I get stuck?',
    answer: 'Each module includes clear visual guides showing exactly where to find each setting and what the result should look like. If you need additional help, you can contact support.'
  },
  {
    question: 'What\'s your refund policy?',
    answer: 'Due to the nature of digital content, purchases are generally non-refundable once access is granted. However, we may issue refunds for duplicate payments, technical issues, or as required under Australian Consumer Law. See our full Refund Policy for details.'
  },
  {
    question: 'Will you access my accounts or data?',
    answer: 'No. This is guided instruction only. We never ask for your passwords or login credentials. You make all changes yourself, on your own devices, while following along with the video.'
  }
];

const getDefaultLandingContent = (course) => ({
  tagline: 'A practical, non-technical course to reduce everyday digital risk',
  duration: '50–55 minutes'
});

export default CourseLanding;
